// frontend/src/pages/StudentHistoryPage.js
// API used:
//   GET /api/students/:id
//   GET /api/students/:id/history  (house points, reasons, timestamps)
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "../api/client";

/* Tiny breakpoint hook */
function useIsMobile(breakpoint = 768) {
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth < breakpoint : false
  );
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < breakpoint);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [breakpoint]);
  return isMobile;
}

export default function StudentHistoryPage() {
  const { id } = useParams();
  const isMobile = useIsMobile();

  const [student, setStudent] = useState(null);
  const [history, setHistory] = useState([]);
  const [busy, setBusy] = useState(true);
  const [err, setErr] = useState("");

  useEffect(() => {
    let alive = true;
    (async () => {
      setBusy(true);
      setErr("");

      const [s, h] = await Promise.all([
        api.get(`/api/students/${id}`),
        api.get(`/api/students/${id}/history`),
      ]);

      if (!alive) return;

      if (!s.ok) {
        setErr(s?.data?.detail || "Unable to load student.");
        setBusy(false);
        return;
      }
      if (!h.ok) {
        setErr(h?.data?.detail || "Unable to load history.");
        setBusy(false);
        return;
      }

      setStudent(s.data);
      const rows = Array.isArray(h.data?.results)
        ? h.data.results
        : Array.isArray(h.data)
        ? h.data
        : [];
      setHistory(rows);
      setBusy(false);
    })();
    return () => {
      alive = false;
    };
  }, [id]);

  const sx = computeStyles(isMobile);

  return (
    <div style={sx.page}>
      <h1 style={sx.h1}>Student History</h1>

      {busy && <div>Loading…</div>}
      {err && <div style={sx.error}>{err}</div>}

      {!busy && !err && student && (
        <div style={sx.card}>
          <div style={sx.title}>
            {student.first_name} {student.last_name}
          </div>
          {student.email && <div style={{ color: "#555" }}>{student.email}</div>}
        </div>
      )}

      {!busy && !err && (
        <div style={sx.tableCard}>
          <div style={sx.tableScroll}>
            <table style={sx.table}>
              <thead>
                <tr>
                  <th style={sx.th}>When</th>
                  <th style={sx.th}>Points</th>
                  <th style={sx.th}>Habit</th>
                  <th style={sx.th}>Reason</th>
                  <th style={sx.th}>Awarded By</th>
                </tr>
              </thead>
              <tbody>
                {history.length === 0 && (
                  <tr>
                    <td colSpan={5} style={{ padding: 12 }}>
                      No history yet.
                    </td>
                  </tr>
                )}
                {history.map((r, idx) => (
                  <tr key={r.id ?? idx}>
                    <td style={sx.td}>{r.created_at ?? r.timestamp}</td>
                    <td style={sx.td}>{r.points}</td>
                    <td style={sx.td}>{r.habit_name ?? r.habit?.name}</td>
                    <td style={sx.td}>{r.reason_text ?? r.reason?.text}</td>
                    <td style={sx.td}>{r.teacher_name ?? r.awarded_by?.name}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function computeStyles(isMobile) {
  return {
    page: { display: "grid", gap: 16 },
    h1: { margin: 0, fontSize: 20, color: "#003e73", fontWeight: 800 },
    title: { fontWeight: 700, color: "#003e73" },

    card: {
      background: "#fff",
      border: "1px solid rgba(0,0,0,0.08)",
      borderRadius: 12,
      padding: 12,
    },

    error: {
      color: "#b00020",
      background: "#FFEAEA",
      border: "1px solid #F5B5B5",
      borderRadius: 8,
      padding: "8px 10px",
    },

    /* Card container for the table so borders/rounding stay intact */
    tableCard: {
      background: "#fff",
      borderRadius: 12,
      border: "1px solid rgba(0,0,0,0.08)",
      overflow: "hidden",
    },

    /* Horizontal scroll on small screens */
    tableScroll: {
      width: "100%",
      overflowX: "auto",
      WebkitOverflowScrolling: "touch",
    },

    table: {
      width: "100%",
      borderCollapse: "collapse",
      minWidth: isMobile ? 640 : "auto", // keep columns readable on phones
    },
    th: {
      textAlign: "left",
      padding: 10,
      borderBottom: "1px solid rgba(0,0,0,0.08)",
      background: "#f7f9fc",
      whiteSpace: "nowrap",
    },
    td: {
      padding: 10,
      borderBottom: "1px solid rgba(0,0,0,0.06)",
      whiteSpace: "nowrap",
    },
  };
}
